import React, { useMemo } from 'react';
import { Space, InputNumber, Input, Button, Switch, Typography, Select } from 'antd';

const { Text } = Typography;

/**
 * SegmentsEditor
 * Controlled editor for differential characteristic segments (Id–Ir).
 * Each segment: { slope: number, threshold: number|Infinity, label?: string, note?: string }
 */
const SegmentsEditor = ({ value, onChange, allowInfinity = true }) => {
  const segments = Array.isArray(value) && value.length > 0
    ? value
    : [
        { slope: 0, threshold: 0.4, label: 'K=0', note: '' },
        { slope: 0.5, threshold: Infinity, label: 'K=0.5', note: '' }
      ];

  const emit = (next) => {
    const sorted = [...next].sort((a, b) => {
      const ta = (a.threshold === Infinity) ? Number.MAX_SAFE_INTEGER : Number(a.threshold) || 0;
      const tb = (b.threshold === Infinity) ? Number.MAX_SAFE_INTEGER : Number(b.threshold) || 0;
      return ta - tb;
    });
    onChange && onChange(sorted);
  };

  const addSegment = () => {
    const last = segments[segments.length - 1] || {};
    const nextTh = last.threshold === Infinity ? Infinity : ((Number(last.threshold) || 0) + 0.5);
    emit([...segments, { slope: Number(last.slope) || 0.5, threshold: nextTh, label: '', note: '' }]);
  };

  const updateSegment = (idx, patch) => {
    const next = segments.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    emit(next);
  };

  const removeSegment = (idx) => {
    const next = segments.filter((_, i) => i !== idx);
    emit(next);
  };

  const rows = useMemo(() => segments, [segments]);

  return (
    <div>
      <div style={{ marginBottom: 8, color: '#666' }}>
        <Text>从差动定值开始，阈值递增；末段可设为∞以延伸。</Text>
      </div>
      {rows.map((seg, idx) => {
        const isInf = seg.threshold === Infinity;
        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Input
              style={{ width: 120 }}
              placeholder="标签"
              value={seg.label || ''}
              onChange={(e) => updateSegment(idx, { label: e.target.value })}
            />
            <Space size={4} align="center">
              <Text type="secondary">K</Text>
              <InputNumber
                min={0}
                step={0.01}
                value={typeof seg.slope === 'number' ? seg.slope : undefined}
                onChange={(v) => updateSegment(idx, { slope: (v ?? 0) })}
              />
            </Space>
            <Space size={4} align="center">
              <Text type="secondary">Ir阈值(A)</Text>
              <InputNumber
                style={{ width: 140 }}
                min={0}
                step={0.01}
                disabled={allowInfinity && isInf}
                value={isInf ? undefined : (typeof seg.threshold === 'number' ? seg.threshold : undefined)}
                onChange={(v) => updateSegment(idx, { threshold: (v ?? 0) })}
              />
              {allowInfinity && (
                <Switch
                  checkedChildren="∞"
                  unCheckedChildren="数值"
                  checked={isInf}
                  onChange={(checked) => updateSegment(idx, { threshold: checked ? Infinity : (Number(seg.threshold) || 0) })}
                />
              )}
            </Space>
            <Input
              style={{ flex: 1 }}
              placeholder="备注/说明（建议≤120字符）"
              maxLength={240}
              value={seg.note || ''}
              onChange={(e) => updateSegment(idx, { note: e.target.value })}
            />
            <Text type={(seg.note || '').length > 120 ? 'danger' : 'secondary'}>
              {(seg.note || '').length}/120
            </Text>
            <Select
              size="small"
              style={{ width: 140 }}
              placeholder="插入模板"
              options={[
                { value: '合并段', label: '合并段' },
                { value: '验算上限', label: '验算上限' },
                { value: '来自设备设定', label: '来自设备设定' },
                { value: '需现场复核', label: '需现场复核' },
                { value: '制造商典型值', label: '制造商典型值' },
                { value: '暂未核实', label: '暂未核实' },
                { value: '参考历史曲线', label: '参考历史曲线' }
              ]}
              onChange={(val) => {
                const base = (seg.note || '').trim();
                const next = base ? `${base}；${val}` : val;
                updateSegment(idx, { note: next });
              }}
            />
            <Button danger onClick={() => removeSegment(idx)}>删除</Button>
          </div>
        );
      })}
      <div>
        <Button type="dashed" onClick={addSegment}>新增分段</Button>
      </div>
    </div>
  );
};

export default SegmentsEditor;